export type MahnStufe = '1_mahnung' | '2_mahnung' | 'inkasso' | 'anwalt';

export const MAHNUNG_STUFEN: { value: MahnStufe; label: string }[] = [
  { value: '1_mahnung', label: '1. Mahnung' },
  { value: '2_mahnung', label: '2. Mahnung' },
  { value: 'inkasso', label: 'Inkasso' },
  { value: 'anwalt', label: 'Anwalt' },
];

export interface QuickActionDraftInput {
  aktenNr: string;
  versicherungEmail: string | null;
}

export interface QuickActionDraftOutput {
  subject: string;
  body_text: string;
  body_html: string;
  recipient: string;
  requiresRecipient: boolean;
}

const RECIPIENT_MISSING =
  'HINWEIS: Empfänger fehlt — E-Mail der Versicherung bitte vor dem Versand ergänzen.';

function resolveRecipient(email: string | null): { recipient: string; requiresRecipient: boolean } {
  const recipient = email?.trim() || '';
  return { recipient, requiresRecipient: !recipient };
}

function missingRecipientHtml(): string {
  return `<p style="background:#FEF3C7;border-left:4px solid #F59E0B;padding:8px 12px;margin:0 0 12px 0;font-weight:600;color:#92400E;">${RECIPIENT_MISSING}</p>`;
}

export function buildAnschreibenDraft(input: QuickActionDraftInput): QuickActionDraftOutput {
  const { aktenNr, versicherungEmail } = input;
  const { recipient, requiresRecipient } = resolveRecipient(versicherungEmail);

  const subject = `Erstes Anschreiben – ${aktenNr}`;

  const bodyLines = [
    'Sehr geehrte Damen und Herren,',
    '',
    `wir melden uns in Bezug auf o.g. Schadenfall (Aktenzeichen ${aktenNr}) und bitten Sie, die Regulierung zeitnah vorzunehmen.`,
    '',
    'Wir stehen Ihnen für Rückfragen gerne zur Verfügung.',
    '',
    'Mit freundlichen Grüßen',
  ];
  if (requiresRecipient) bodyLines.unshift(RECIPIENT_MISSING, '');
  const body_text = bodyLines.join('\n');

  const warnBlock = requiresRecipient ? missingRecipientHtml() : '';
  const body_html =
    warnBlock +
    `<p>Sehr geehrte Damen und Herren,</p>` +
    `<p>wir melden uns in Bezug auf o.g. Schadenfall (Aktenzeichen <strong>${aktenNr}</strong>) und bitten Sie, die Regulierung zeitnah vorzunehmen.</p>` +
    `<p>Wir stehen Ihnen für Rückfragen gerne zur Verfügung.</p>` +
    `<p>Mit freundlichen Grüßen</p>`;

  return { subject, body_text, body_html, recipient, requiresRecipient };
}

const MAHNUNG_LABEL: Record<MahnStufe, string> = {
  '1_mahnung': '1. Mahnung',
  '2_mahnung': '2. Mahnung',
  inkasso: 'Inkasso',
  anwalt: 'Anwalt',
};

const MAHNUNG_BODY: Record<MahnStufe, string> = {
  '1_mahnung': 'Trotz unserer Schreiben haben wir bislang keine Regulierung erhalten. Wir bitten Sie dringend, den ausstehenden Betrag unverzüglich zu begleichen.',
  '2_mahnung': 'Wir mahnen Sie ein zweites Mal zur Begleichung des ausstehenden Betrags auf. Sollte keine Zahlung eingehen, behalten wir uns weitere rechtliche Schritte vor.',
  inkasso: 'Wir haben die Angelegenheit an unser Inkassobüro übergeben. Bitte klären Sie die offene Forderung direkt dort.',
  anwalt: 'Wir haben die Angelegenheit an unsere Rechtsanwälte übergeben. Weitere Korrespondenz bitte direkt mit den beauftragten Anwälten.',
};

export function buildMahnungDraft(stufe: MahnStufe, input: QuickActionDraftInput): QuickActionDraftOutput {
  const { aktenNr, versicherungEmail } = input;
  const { recipient, requiresRecipient } = resolveRecipient(versicherungEmail);
  const label = MAHNUNG_LABEL[stufe];
  const bodyText = MAHNUNG_BODY[stufe];

  const subject = `${label} – ${aktenNr}`;

  const bodyLines = [
    'Sehr geehrte Damen und Herren,',
    '',
    bodyText,
    '',
    'Mit freundlichen Grüßen',
  ];
  if (requiresRecipient) bodyLines.unshift(RECIPIENT_MISSING, '');
  const body_text = bodyLines.join('\n');

  const warnBlock = requiresRecipient ? missingRecipientHtml() : '';
  const body_html =
    warnBlock +
    `<p>Sehr geehrte Damen und Herren,</p>` +
    `<p>${bodyText}</p>` +
    `<p>Mit freundlichen Grüßen</p>`;

  return { subject, body_text, body_html, recipient, requiresRecipient };
}

export function buildSachstandDraft(input: QuickActionDraftInput): QuickActionDraftOutput {
  const { aktenNr, versicherungEmail } = input;
  const { recipient, requiresRecipient } = resolveRecipient(versicherungEmail);

  const subject = `Sachstandsanfrage – ${aktenNr}`;

  const bodyLines = [
    'Sehr geehrte Damen und Herren,',
    '',
    `wir bitten Sie, uns den aktuellen Bearbeitungsstand zum o.g. Schadenfall (Aktenzeichen ${aktenNr}) mitzuteilen.`,
    '',
    'Für Rückfragen stehen wir Ihnen gerne zur Verfügung.',
    '',
    'Mit freundlichen Grüßen',
  ];
  if (requiresRecipient) bodyLines.unshift(RECIPIENT_MISSING, '');
  const body_text = bodyLines.join('\n');

  const warnBlock = requiresRecipient ? missingRecipientHtml() : '';
  const body_html =
    warnBlock +
    `<p>Sehr geehrte Damen und Herren,</p>` +
    `<p>wir bitten Sie, uns den aktuellen Bearbeitungsstand zum o.g. Schadenfall (Aktenzeichen <strong>${aktenNr}</strong>) mitzuteilen.</p>` +
    `<p>Für Rückfragen stehen wir Ihnen gerne zur Verfügung.</p>` +
    `<p>Mit freundlichen Grüßen</p>`;

  return { subject, body_text, body_html, recipient, requiresRecipient };
}
